import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseTabs from 'components/atoms/base-tabs';
import { ModuleType } from 'infrastructure/enums';
import { usePatientKeyNotes } from 'infrastructure/hooks/patient/use-key-notes';
import BaseLoader from 'components/atoms/base-loader';
import ChatDotsIcon from 'components/atoms/icons/chat-dots';
import BaseButton from 'components/atoms/baseButton';
import PencilIcon from 'components/atoms/icons/pencilIcon';
import BaseRichTextEditor from 'components/atoms/base-rich-text-editor';
import useKey from 'infrastructure/hooks/utils/use-key';
import classNames from 'classnames';
import BaseRichText from 'components/atoms/base-rich-text-editor/text';

import s from './styles.module.scss';

import type { IBaseTab } from 'components/atoms/base-tabs';

interface IProps {
  patientGuid: string;
}

const CallDashboardKeyNotesForm: React.FC<IProps> = (props) => {
  const { patientGuid } = props;

  const { t } = useTranslation();

  const tabs: IBaseTab<ModuleType.RPM | ModuleType.CCM>[] = [
    {
      key: ModuleType.RPM,
      label: t('labels.rpm'),
    },
    {
      key: ModuleType.CCM,
      label: t('labels.ccm'),
    },
  ];

  const { key, changeKey } = useKey();
  const [isEdit, setIsEdit] = useState(false);
  const [tab, setTab] = useState(tabs[0]);
  const [note, setNote] = useState('');

  const { data, loading, update } = usePatientKeyNotes({
    patientGuid,
    module: tab.key,
  });

  const wrapperCls = classNames(s['note-wrapper'], {
    [s.editor]: isEdit,
  });

  const Empty = () => (
    <div className={s.empty}>
      <ChatDotsIcon />
      <span>{t('labels.thereAreNoKeyNotes')}</span>
    </div>
  );

  const handleCancel = () => {
    setIsEdit(false);
    setNote('');
    changeKey();
  };

  const handleEdit = () => {
    setIsEdit(true);
  };

  const handleSave = async () => {
    await update({
      note,
      module: tab.key,
    });
    setIsEdit(false);
  };

  return (
    <div>
      <div className={s.head}>
        <span className={s.title}>{t('labels.keyNotes')}</span>
        <BaseTabs tabs={tabs} size="small" type="rounded" onChange={setTab} />
      </div>

      <BaseLoader loading={loading}>
        <div className={wrapperCls} key={key}>
          {isEdit ? (
            <BaseRichTextEditor
              height="500px"
              value={data.note}
              setValue={setNote}
              disabled={loading}
            />
          ) : (
            <div className={s.note}>
              {data.note.trim() ? (
                <BaseRichText value={data.note.trim()} />
              ) : (
                <Empty />
              )}
            </div>
          )}
        </div>

        <div className={s.footer}>
          {isEdit ? (
            <>
              <BaseButton
                width="120px"
                type="secondary"
                label={t('controls.cancel')}
                onClick={handleCancel}
              />
              <BaseButton
                width="120px"
                label={t('controls.save')}
                onClick={handleSave}
              />
            </>
          ) : (
            <BaseButton
              width="170px"
              label={t('controls.editKeyNote')}
              icon={<PencilIcon />}
              onClick={handleEdit}
            />
          )}
        </div>
      </BaseLoader>
    </div>
  );
};

export default CallDashboardKeyNotesForm;
